import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  input,
  model,
  output,
  OutputEmitterRef,
  OutputRefSubscription,
  signal,
} from '@angular/core';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import {
  FileService,
  notify,
  SiaArchivePath,
  SiaTooltipDirective,
  SiaTranslatePipe,
  YesNoDialogComponent,
} from '@sia/shared';
import { firstValueFrom } from 'rxjs';
import { SiaBaseEditorSignalComponent } from '../sia-base-editor/sia-base-editor-signal.component';

@Component({
  selector: 'sia-input-file',
  templateUrl: './sia-input-file.component.html',
  styleUrls: ['./sia-input-file.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatButton,
    SiaTranslatePipe,
    MatIcon,
    SiaTooltipDirective,
    MatIconButton,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
  ],
})
export class SiaInputFileComponent extends SiaBaseEditorSignalComponent {
  private siaFileUtilsService = inject(FileService);

  private _canDestroy = true;
  private _timeouts: any[] = [];

  changed = signal(false);
  deleted = signal(false);
  removed = signal<string[]>([]);
  originalFiles = signal<string[] | undefined>(undefined);

  // Output emitters
  fileNameChange = output<string | undefined>();

  // Event subscriptions
  private _dataUpdatedEventSubscription?: OutputRefSubscription;
  private _dataInsertedEventSubscription?: OutputRefSubscription;
  private _dataDeletedEventSubscription?: OutputRefSubscription;
  private _dataCancelEventSubscription?: OutputRefSubscription;

  allowedExtensions = input('file');
  fixedFileName = input<string | undefined>();
  uploadIcon = input('upload');

  fileName = model<string | undefined>();

  uploadFolder = input<string | undefined, string | undefined>(undefined, {
    transform: (value: string | undefined) => {
      const normalized = value?.replace('/', '\\');
      if (!normalized) return normalized;
      return normalized.endsWith('\\') ? normalized : normalized + '\\';
    },
  });

  dataUpdatedEvent = input<OutputEmitterRef<any> | undefined>();
  dataInsertedEvent = input<OutputEmitterRef<any> | undefined>();
  dataDeletedEvent = input<OutputEmitterRef<any> | undefined>();
  dataCancelEvent = input<OutputEmitterRef<any> | undefined>();

  allowMultipleFiles = input(true);

  constructor() {
    super();

    effect(
      () => {
        const name = this.fileName();
        this.fileNameChange.emit(name);
        this.loadExistingFiles();
      },
      { allowSignalWrites: true },
    );

    let lastUploadFolder: string | undefined;
    effect(
      () => {
        const folder = this.uploadFolder();
        if (folder === lastUploadFolder) return;
        lastUploadFolder = folder;
        if (folder === undefined) return;

        this.value.set(null as any);
        this.loadExistingFiles();
      },
      { allowSignalWrites: true },
    );

    effect((onCleanup) => {
      this._dataUpdatedEventSubscription?.unsubscribe();
      const ref = this.dataUpdatedEvent();
      this._dataUpdatedEventSubscription = ref?.subscribe(() =>
        this.handleUpdateEvent(),
      );
      onCleanup(() => this._dataUpdatedEventSubscription?.unsubscribe());
    });

    effect((onCleanup) => {
      this._dataInsertedEventSubscription?.unsubscribe();
      const ref = this.dataInsertedEvent();
      this._dataInsertedEventSubscription = ref?.subscribe(() =>
        this.handleInsertEvent(),
      );
      onCleanup(() => this._dataInsertedEventSubscription?.unsubscribe());
    });

    effect((onCleanup) => {
      this._dataDeletedEventSubscription?.unsubscribe();
      const ref = this.dataDeletedEvent();
      this._dataDeletedEventSubscription = ref?.subscribe(() =>
        this.handleDeleteEvent(),
      );
      onCleanup(() => this._dataDeletedEventSubscription?.unsubscribe());
    });

    effect((onCleanup) => {
      this._dataCancelEventSubscription?.unsubscribe();
      const ref = this.dataCancelEvent();
      this._dataCancelEventSubscription = ref?.subscribe(() =>
        this.handleCancelEvent(),
      );
      onCleanup(() => this._dataCancelEventSubscription?.unsubscribe());
    });
  }

  private getFolderName(): string {
    const currentLabel = this.label();
    return currentLabel
      ? currentLabel.toLowerCase().replace(/\s+/g, '')
      : 'files';
  }

  private async loadExistingFiles(force: boolean = false) {
    try {
      const folder = this.uploadFolder();
      const name = this.fileName();
      if (!folder || !name || (this.value() && !force)) {
        return;
      }

      // Check if fileName has an extension
      const hasExtension = name.includes('.');

      if (hasExtension) {
        this.value.set([{ name }] as File[]);
        this.originalFiles.set([name]);
        return;
      }

      const path = folder + name;
      const files = await this.siaFileUtilsService.getFilesNames(path, true);

      if (files && files.length > 0) {
        const fileArray = files.map((file) => ({ name: file }) as File);
        this.value.set(fileArray);
        this.originalFiles.set(files);
      } else {
        this.value.set(undefined);
        this.originalFiles.set(undefined);
        this.fileName.set(undefined);
      }
    } catch (error) {
      console.error('Failed to load existing files', error);
    }
  }

  private async handleUpdateEvent() {
    this._canDestroy = false;
    try {
      const currentValue = this.value();
      if (!currentValue || currentValue.length === 0) {
        await this.cleanupOriginalFiles();
        return;
      }

      const currentFileNames = currentValue.map((file: File) => file.name);
      const originals = this.originalFiles();
      if (
        originals?.length &&
        originals.length > 1 &&
        currentFileNames.length === 1
      ) {
        await this.handleMultipleToSingleFile();
        return;
      }
      if (
        originals?.length &&
        originals.length === 1 &&
        currentFileNames.length > 1
      ) {
        await this.handleSingleToMultipleFile();
        return;
      }

      await this.deleteRemovedFiles();

      if (this.noFileChangesNeeded(currentFileNames)) {
        this.originalFiles.set(currentFileNames);
        return;
      }
      await this.uploadFiles();

      this.originalFiles.set(currentFileNames);
    } catch (error) {
      notify('Impossibile caricare il file', 2000, 'error');
      console.error('Error during file upload', error);
    } finally {
      this._canDestroy = true;
      const refreshed = this.value()?.map((file: File) => file.name);
      this.originalFiles.set(refreshed);
    }
  }

  private async handleInsertEvent() {
    this._canDestroy = false;

    try {
      const currentValue = this.value();
      if (!currentValue || !this.uploadFolder() || currentValue.length === 0) {
        return;
      }

      await this.uploadFiles();
      this.originalFiles.set(currentValue.map((file: File) => file.name));
    } catch (error) {
      notify('Impossibile caricare il file', 2000, 'error');
    } finally {
      this._canDestroy = true;
    }
  }

  private async handleDeleteEvent() {
    this._canDestroy = false;
    try {
      await Promise.all(
        this.originalFiles()?.map((file) => this.deleteFile(file, false)) || [],
      );
    } catch (error) {
      console.error('Errore durante l`eliminazione dei file', error);
    } finally {
      this._canDestroy = true;
    }
  }

  private async handleCancelEvent() {
    this.deleted.set(false);
    this.removed.set([]);
    this.changed.set(false);
    this.value.set(undefined);
    this.loadExistingFiles(true);
  }

  private async cleanupOriginalFiles() {
    const originals = this.originalFiles();
    const folder = this.uploadFolder();
    if (!originals || !folder) return;

    if (originals.length > 1) {
      await this.siaFileUtilsService.deleteFile(
        folder + '\\' + this.getFolderName(),
        true,
        false,
      );
    } else {
      await this.siaFileUtilsService.deleteFile(
        folder + '\\' + originals[0],
        true,
        false,
      );
    }
  }

  private async deleteRemovedFiles() {
    const originals = this.originalFiles();
    if (this.deleted() && originals?.length) {
      for (const file of originals) {
        await this.deleteFile(file, false);
      }
      return;
    }
    for (const file of this.removed()) {
      await this.deleteFile(file, false);
    }
  }

  private async handleMultipleToSingleFile() {
    await this.siaFileUtilsService.deleteFile(
      (this.uploadFolder() ?? '') + this.getFolderName(),
      true,
      false,
    );
    const currentValue = this.value();
    if (currentValue?.length) {
      this.originalFiles.set([currentValue[0].name]);
    }
    this.uploadFiles();
  }
  private async handleSingleToMultipleFile() {
    await this.siaFileUtilsService.moveFileAndDelete(
      (this.uploadFolder() ?? '') + (this.value() ? this.value()![0].name : ''),
      (this.uploadFolder() ?? '') + this.getFolderName(),
      '',
      true,
    );
    this.uploadFiles();
  }
  private noFileChangesNeeded(currentNames: string[]): boolean {
    if (
      !this.uploadFolder() ||
      currentNames.length === 0 ||
      !this.value() ||
      this.value()!.length === 0
    ) {
      return true;
    }
    if (this.deleted() || this.removed().length > 0 || this.changed())
      return false;

    const originals = this.originalFiles();
    return (
      !!originals?.length &&
      currentNames.every((name) => originals!.includes(name))
    );
  }

  async uploadFiles() {
    const currentValue = this.value();
    if (!currentValue || !currentValue.length) return;

    if (currentValue.length > 1) {
      // For multiple files
      const folderPath =
        (this.uploadFolder() ?? '') + (this.fileName() ?? '') + '\\';
      for (const file of currentValue) {
        if (file && file?.size && file?.size > 0) {
          const archivePath: SiaArchivePath = {
            isRelativePath: true,
            isSystemArchive: false,
            path: folderPath,
          };
          try {
            await this.siaFileUtilsService.uploadFile(archivePath, file, false);
          } catch (error) {
            console.error('Errore nel caricamento del file:', error);
            notify(
              `Errore nel caricamento del file ${file.name}.`,
              2000,
              'error',
            );
            return;
          }
        }
      }
      notify('File caricati con successo', 2000, 'success');
    } else {
      if (this.value()[0] && this.value()[0].size && this.value()[0].size > 0) {
        const archivePath: SiaArchivePath = {
          isRelativePath: true,
          isSystemArchive: false,
          path: this.uploadFolder() ?? '',
        };
        try {
          await this.siaFileUtilsService.uploadFile(
            archivePath,
            this.value()[0],
            false,
          );
        } catch (error) {
          console.error('Errore nel caricamento del file:', error);
          notify(
            `Errore nel caricamento del file ${this.value()[0].name}.`,
            2000,
            'error',
          );
          return;
        }
      }
      notify('File caricato con successo', 2000, 'success');
    }
    const currentFileNames = currentValue.map((file: File) => file.name);
    this.changed.set(false);
    this.deleted.set(false);
    this.removed.set([]);
    this.originalFiles.set(
      Array.isArray(currentFileNames)
        ? [...currentFileNames]
        : [currentFileNames],
    );
  }

  async chooseFile(files: FileList | null) {
    if (!files || files.length === 0) return;
    this.processFiles(files);

    this.cdr.detectChanges();
  }

  private processFiles(files: FileList): void {
    const filesArray: File[] = [];

    // Get existing file names for duplicate checking
    const currentValue = this.value() as { name: string }[] | undefined;
    const existingFileNames = currentValue
      ? currentValue.map((f) => f.name)
      : [];

    let valueBiggestIndex = currentValue
      ? currentValue.reduce((max, file) => {
          const fileWithoutExtension = file.name
            .split('.')
            .slice(0, -1)
            .join('.');
          const index = parseInt(fileWithoutExtension.split('_').pop() || '0');
          return Math.max(max, index);
        }, -1)
      : -1;
    if (!this.allowMultipleFiles()) {
      valueBiggestIndex = -1;
    }
    for (let i = 0; i < files.length; i++) {
      let file = files[i];
      let finalFileName = file.name;

      if (this.fixedFileName()) {
        const extension = file.name.split('.').pop();
        finalFileName = `${this.fixedFileName()}_${valueBiggestIndex >= 0 ? valueBiggestIndex + 1 + i : i}.${extension}`;
        file = new File([file], finalFileName, {
          type: file.type,
        });
      }

      // Only add if file name doesn't already exist
      if (!existingFileNames.includes(finalFileName)) {
        filesArray.push(file);
      }
    }

    if (!this.allowMultipleFiles()) {
      const nextValue = filesArray.length > 0 ? [filesArray[0]] : currentValue;
      this.value.set(nextValue as any);
      if (filesArray.length > 0) {
        this.fileName.set(filesArray[0].name);
      }
    } else if (filesArray.length > 0) {
      const tmp = !currentValue
        ? filesArray
        : (currentValue as File[]).concat(filesArray);
      this.value.set(tmp as any);
      const updatedValue = this.value();
      if (updatedValue?.length) {
        this.fileName.set(
          updatedValue.length > 1
            ? this.getFolderName()
            : (updatedValue as File[])[0].name,
        );
      }
    }

    if (filesArray.length > 0) {
      this.changed.set(true);
    }
  }

  async deleteFile(fileName: string, showDialog = false) {
    if (showDialog) {
      const dialogRef = this.dialog.open(YesNoDialogComponent, {
        data: { title: 'Tutti i file verranno eliminati, vuoi continuare?' },
      });

      if (await firstValueFrom(dialogRef.afterClosed())) {
        this.fileName.set(undefined);
        this.value.set(undefined);
      } else {
        return;
      }
    }

    if (!fileName || !this.uploadFolder()) return;

    const folder = this.uploadFolder();
    const originals = this.originalFiles();
    if (!folder) return;

    const filePath =
      originals?.length && originals.length > 1
        ? folder + this.getFolderName() + '\\' + fileName
        : folder + fileName;

    await this.siaFileUtilsService.deleteFile(filePath, true, false);
  }

  downloadFile(fileName?: string): void {
    if (!this.uploadFolder() || (!fileName && !this.fileName())) return;

    try {
      const fileToDownload = fileName || this.fileName()!;
      const folder = this.uploadFolder()!;
      const path = fileName
        ? this.uploadFolder + this.getFolderName() + '\\' + fileName
        : this.uploadFolder + fileToDownload;
      this.siaFileUtilsService.downloadFile(path, fileToDownload, true, false);
    } catch (error) {
      notify('Impossibile scaricare il file', 2000, 'error');
    }
  }

  removeFile(file: File): void {
    const currentValue = this.value() ?? [];
    const nextValue = currentValue.filter((f: File) => f.name !== file.name);
    this.value.set(nextValue as any);
    this.fileName.set(
      nextValue.length > 1 ? this.getFolderName() : nextValue[0]?.name,
    );
    this.removed.update((prev) => [...prev, file.name]);
  }

  clearValue(): void {
    const inputElement = document.getElementById(
      this.id()!,
    ) as HTMLInputElement;
    if (inputElement) inputElement.value = '';
    this.deleted.set(true);
    this.fileName.set(undefined);
    this.value.set(undefined);
  }

  async downloadFolder() {
    const folder = this.uploadFolder();
    if (!folder || !this.originalFiles()) return;

    try {
      await this.siaFileUtilsService.downloadFilesAsZip(
        folder + this.getFolderName() + '\\',
        this.label() ?? 'files',
        true,
      );
    } catch (error) {}
  }

  ngOnDestroy(): void {
    // Use a more efficient approach than polling
    Promise.resolve().then(() => {
      if (this._canDestroy) {
        this.cleanupSubscriptions();
        this._timeouts.forEach((t) => clearTimeout(t));
        this._timeouts = [];
        super.ngOnDestroy();
      } else {
        // If operations in progress, try again after a short delay
        let t = setTimeout(() => this.ngOnDestroy(), 100);
        this._timeouts.push(t);
      }
    });
  }

  private cleanupSubscriptions(): void {
    this._dataInsertedEventSubscription?.unsubscribe();
    this._dataUpdatedEventSubscription?.unsubscribe();
    this._dataDeletedEventSubscription?.unsubscribe();
    this._dataCancelEventSubscription?.unsubscribe();
  }
}
